import {Injectable, OnDestroy, signal} from '@angular/core';

/** Una domanda non richiede di piu': oltre si ferma da solo, e la dimensione dell'audio resta contenuta. */
export const MAX_RECORDING_MS = 60_000;

/**
 * Sotto questo volume (RMS) l'audio e' praticamente muto: microfono spento o disattivato.
 * Soglia volutamente bassissima: una voce anche debole la supera. Le frasi che Whisper inventa
 * sul silenzio le scarta il motore AI.
 */
const SILENCE_RMS = 0.002;
const LEVEL_SAMPLE_MS = 100;

/** Formati preferiti, in ordine: Chrome/Firefox registrano webm, Safari (anche iPhone) solo mp4. */
const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];

/** starting = in attesa del permesso del microfono */
export type VoiceStatus = 'idle' | 'starting' | 'recording';

/** Errori come chiavi i18n, mostrate dal componente. */
export type VoiceError = 'voice.error_denied' | 'voice.error_unavailable' | 'voice.error_empty' | 'voice.error_no_speech';

export interface VoiceRecording {
  audio: Blob;
  filename: string;
}

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return undefined;
  }
  return MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
}

function extensionFor(mimeType: string): string {
  if (mimeType.includes('mp4')) return 'm4a';
  if (mimeType.includes('ogg')) return 'ogg';
  return 'webm';
}

/**
 * Registra la domanda dal microfono (logica ripresa da llm_immagini, useVoiceRecorder).
 * L'audio registrato viene restituito al componente, che lo fa trascrivere dal backend.
 * Da fornire nel componente (`providers`), cosi' il microfono si rilascia quando il componente si chiude.
 */
@Injectable()
export class VoiceRecorderService implements OnDestroy {
  /** Il microfono esiste solo nel browser, su HTTPS o localhost. */
  readonly supported = typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== 'undefined';

  readonly status = signal<VoiceStatus>('idle');
  /** Secondi trascorsi dall'inizio della registrazione. */
  readonly elapsed = signal(0);

  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private stopTimer: ReturnType<typeof setTimeout> | null = null;
  private tick: ReturnType<typeof setInterval> | null = null;
  private cancelled = false;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private levelTimer: ReturnType<typeof setInterval> | null = null;
  /** Volume massimo misurato; null = misura non disponibile (l'audio si invia comunque) */
  private maxRms: number | null = null;
  private resolveStop: ((recording: VoiceRecording | null) => void) | null = null;

  /**
   * Avvia la registrazione. La promessa si risolve quando la registrazione finisce
   * (stop, limite di durata) con l'audio, o con null se annullata.
   * `onStream` riceve il microfono appena aperto (per l'anteprima in diretta).
   */
  async start(onStream?: (stream: MediaStream) => void): Promise<VoiceRecording | null> {
    if (this.status() !== 'idle') {
      return null;
    }
    this.cancelled = false;
    this.status.set('starting');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({audio: true});
    } catch (err) {
      this.status.set('idle');
      const denied = err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'SecurityError');
      throw (denied ? 'voice.error_denied' : 'voice.error_unavailable') as VoiceError;
    }

    if (this.cancelled) {
      stream.getTracks().forEach((track) => track.stop());
      this.status.set('idle');
      return null;
    }
    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? {mimeType} : undefined);
    const chunks: Blob[] = [];
    this.stream = stream;
    this.recorder = recorder;
    this.watchLevel(stream);
    onStream?.(stream);

    return new Promise<VoiceRecording | null>((resolve, reject) => {
      this.resolveStop = resolve;
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      recorder.onstop = () => {
        const cancelled = this.cancelled;
        // Senza misura del volume (browser senza Web Audio, audio non avviato) l'audio si invia comunque
        const silent = this.maxRms !== null && this.maxRms < SILENCE_RMS;
        this.release();
        if (cancelled) {
          resolve(null);
          return;
        }
        const type = recorder.mimeType || mimeType || 'audio/webm';
        const audio = new Blob(chunks, {type});
        if (audio.size === 0) {
          reject('voice.error_empty' as VoiceError);
          return;
        }
        if (silent) {
          // Microfono muto: inutile inviare (Whisper inventerebbe frasi come "Grazie a tutti")
          reject('voice.error_no_speech' as VoiceError);
          return;
        }
        resolve({audio, filename: `domanda.${extensionFor(type)}`});
      };

      recorder.start();
      this.status.set('recording');
      this.elapsed.set(0);
      const startedAt = Date.now();
      this.tick = setInterval(() => this.elapsed.set(Math.floor((Date.now() - startedAt) / 1000)), 250);
      this.stopTimer = setTimeout(() => this.stop(), MAX_RECORDING_MS);
    });
  }

  /** Termina la registrazione: l'audio arriva alla promessa di start(). */
  stop(): void {
    if (this.recorder?.state === 'recording') {
      this.recorder.stop();
    }
  }

  /** Annulla: il microfono si spegne e l'audio viene scartato. */
  cancel(): void {
    this.cancelled = true;
    if (this.status() === 'starting') {
      return;  // il microfono non e' ancora acceso: start() vede `cancelled` e si ferma
    }
    if (this.recorder?.state === 'recording') {
      this.recorder.stop();
    } else {
      this.release();
      this.resolveStop?.(null);
    }
  }

  ngOnDestroy(): void {
    this.cancel();
  }

  /** Misura il volume del microfono durante la registrazione, per riconoscere il silenzio. */
  private watchLevel(stream: MediaStream): void {
    this.maxRms = null;
    const Ctx = window.AudioContext ?? (window as any).webkitAudioContext;
    if (!Ctx) {
      return;
    }
    try {
      const context: AudioContext = new Ctx();
      void context.resume?.();
      const analyser = context.createAnalyser();
      analyser.fftSize = 2048;
      context.createMediaStreamSource(stream).connect(analyser);
      const samples = new Float32Array(analyser.fftSize);
      this.audioContext = context;
      this.analyser = analyser;
      this.levelTimer = setInterval(() => {
        if (context.state !== 'running') {
          return;  // es. Safari che non ha avviato l'audio: nessuna misura, nessun blocco
        }
        analyser.getFloatTimeDomainData(samples);
        let sum = 0;
        for (const v of samples) sum += v * v;
        this.maxRms = Math.max(this.maxRms ?? 0, Math.sqrt(sum / samples.length));
      }, LEVEL_SAMPLE_MS);
    } catch {
      this.audioContext = null;
      this.analyser = null;
    }
  }

  private release(): void {
    if (this.levelTimer) clearInterval(this.levelTimer);
    this.levelTimer = null;
    void this.audioContext?.close().catch(() => undefined);
    this.audioContext = null;
    this.analyser = null;
    if (this.stopTimer) clearTimeout(this.stopTimer);
    if (this.tick) clearInterval(this.tick);
    this.stopTimer = null;
    this.tick = null;
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.recorder = null;
    this.resolveStop = null;
    this.status.set('idle');
  }
}
